import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '../../db/shiksha-db';
import { ArrowLeft, UserPlus, Mail, Shield, CheckCircle, Trash2, Plus, X } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';

export function ManageTeachers() {
  const { user } = useAuth();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('teacher');
  const [isClassTeacher, setIsClassTeacher] = useState(false);
  const [classTeacherOf, setClassTeacherOf] = useState('');
  const [classTeacherSection, setClassTeacherSection] = useState('');
  
  const [subjectsAssigned, setSubjectsAssigned] = useState([]);
  const [newSubClass, setNewSubClass] = useState('Class VI');
  const [newSubSection, setNewSubSection] = useState('');
  const [newSubName, setNewSubName] = useState('');
  
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const teachers = useLiveQuery(() => db.teachers.toArray()) || [];

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      const idToken = await user.getIdToken();
      const apiUrl = import.meta.env.VITE_WORKER_API_URL || 'https://shiksha-edge-api.workers.dev';
      
      const response = await fetch(`${apiUrl}/api/admin/create-teacher`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({ 
          email, password, displayName: name, role, 
          isClassTeacher, classTeacherOf, classTeacherSection, subjectsAssigned,
          apiKey: import.meta.env.VITE_FIREBASE_API_KEY
        })
      });

      if (response.ok) {
        const data = await response.json();
        setMessage('User created successfully!');
        // Update local cache
        await db.teachers.add({ 
          uid: data.user?.uid, email, name, role, 
          isClassTeacher, classTeacherOf, classTeacherSection, subjectsAssigned 
        });
        setEmail('');
        setName('');
        setPassword('');
        setIsClassTeacher(false);
        setClassTeacherOf('');
        setClassTeacherSection('');
        setSubjectsAssigned([]);
      } else {
        const data = await response.json();
        setMessage(`Error: ${data.error}`);
      }
    } catch (err) {
      setMessage(`Network error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleAddSubject = () => {
    if (newSubName.trim()) {
      const subjectId = newSubName.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-');
      const finalClassName = newSubSection.trim() ? `${newSubClass}-${newSubSection.trim().toUpperCase()}` : newSubClass;
      setSubjectsAssigned([...subjectsAssigned, { classId: finalClassName, className: finalClassName, subjectName: newSubName.trim(), subjectId }]);
      setNewSubName('');
      setNewSubSection('');
    }
  };

  const removeSubject = (index) => {
    setSubjectsAssigned(subjectsAssigned.filter((_, i) => i !== index));
  };

  const handleDeleteUser = async (uid, teacherEmail) => {
    if (!window.confirm(`Are you sure you want to delete ${teacherEmail}?`)) return;
    try {
      const idToken = await user.getIdToken();
      const apiUrl = import.meta.env.VITE_WORKER_API_URL || 'https://shiksha-edge-api.workers.dev';
      
      const res = await fetch(`${apiUrl}/api/admin/delete-teacher/${uid}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${idToken}` }
      });
      
      if (res.ok) {
        await db.teachers.where('email').equals(teacherEmail).delete();
        setMessage('User deleted successfully!');
      } else {
        const data = await res.json();
        setMessage(`Error deleting user: ${data.error}`);
      }
    } catch (err) {
      setMessage(`Network error: ${err.message}`);
    }
  };

  return (
    <div className="min-h-screen bg-paper pb-8">
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="flex items-center gap-4 mb-8">
          <Link
            to="/admin/dashboard"
            className="p-2 rounded-lg bg-white border border-divider hover:bg-gray-50 transition-colors"
          >
            <ArrowLeft size={20} className="text-ink" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-ink">Manage Teachers</h1>
            <p className="text-sm text-graphite font-secondary">Create and assign roles to staff members.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Create User Form */}
          <div className="bg-white border border-divider rounded-xl p-6 shadow-sm">
            <h2 className="text-lg font-bold text-ink mb-4 flex items-center gap-2">
              <UserPlus size={20} className="text-indigo-600" />
              Add New User
            </h2>

            {message && (
              <div className={`px-4 py-3 rounded-lg mb-4 text-sm font-medium ${message.includes('Error') ? 'bg-red-50 text-red-800' : 'bg-green-50 text-green-800 flex items-center gap-2'}`}>
                {!message.includes('Error') && <CheckCircle size={16} />}
                {message}
              </div>
            )}

            <form onSubmit={handleCreateUser} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-ink mb-1">Full Name</label>
                <input
                  type="text"
                  required
                  value={name}
                  onChange={e => setName(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                  placeholder="e.g. Ramesh Kumar"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-ink mb-1">Email Address</label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                  placeholder="teacher@rsbrtl.edu"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-ink mb-1">Temporary Password</label>
                <input
                  type="password"
                  required
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                  placeholder="At least 6 characters"
                  minLength={6}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-ink mb-1">Role</label>
                <select
                  value={role}
                  onChange={e => setRole(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                >
                  <option value="teacher">Teacher</option>
                  <option value="admin">Administrator</option>
                </select>
              </div>

              {role === 'teacher' && (
                <>
                  <div className="flex items-center gap-2 mt-4">
                    <input
                      type="checkbox"
                      id="isClassTeacher"
                      checked={isClassTeacher}
                      onChange={e => setIsClassTeacher(e.target.checked)}
                      className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <label htmlFor="isClassTeacher" className="text-sm font-medium text-ink">
                      Assign as Class Teacher
                    </label>
                  </div>
                  
                  {isClassTeacher && (
                    <div className="mt-4 flex gap-4">
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-ink mb-1">Class</label>
                        <select
                          value={classTeacherOf}
                          onChange={e => setClassTeacherOf(e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                        >
                          <option value="">Select a Class</option>
                          <option value="Class VI">Class VI</option>
                          <option value="Class VII">Class VII</option>
                          <option value="Class VIII">Class VIII</option>
                          <option value="Class IX">Class IX</option>
                          <option value="Class X">Class X</option>
                        </select>
                      </div>
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-ink mb-1">Section (Optional)</label>
                        <input
                          type="text"
                          value={classTeacherSection}
                          onChange={e => setClassTeacherSection(e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                          placeholder="e.g. A"
                          maxLength={1}
                        />
                      </div>
                    </div>
                  )}

                  {/* Subject Assignments */}
                  <div className="mt-6 border-t border-divider pt-4">
                    <label className="block text-sm font-medium text-ink mb-2">Subject Assignments</label>
                    <div className="flex gap-2 mb-3">
                      <select
                        value={newSubClass}
                        onChange={e => setNewSubClass(e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                      >
                        <option value="Class VI">Class VI</option>
                        <option value="Class VII">Class VII</option>
                        <option value="Class VIII">Class VIII</option>
                        <option value="Class IX">Class IX</option>
                        <option value="Class X">Class X</option>
                      </select>
                      <input
                        type="text"
                        value={newSubSection}
                        onChange={e => setNewSubSection(e.target.value)}
                        placeholder="Sec (e.g. A)"
                        className="w-24 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                        maxLength={1}
                      />
                      <input
                        type="text"
                        value={newSubName}
                        onChange={e => setNewSubName(e.target.value)}
                        placeholder="Subject (e.g. Science)"
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                      />
                      <button
                        type="button"
                        onClick={handleAddSubject}
                        className="p-2 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 rounded-lg transition-colors"
                      >
                        <Plus size={20} />
                      </button>
                    </div>
                    
                    {subjectsAssigned.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {subjectsAssigned.map((sub, idx) => (
                          <div key={idx} className="flex items-center gap-1 bg-gray-100 text-gray-800 px-3 py-1 rounded-full text-xs font-medium">
                            {sub.className} - {sub.subjectName}
                            <button type="button" onClick={() => removeSubject(idx)} className="text-gray-500 hover:text-red-500">
                              <X size={14} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg px-4 py-2 font-semibold transition-colors disabled:opacity-50"
              >
                {loading ? 'Creating...' : 'Create Account'}
              </button>
            </form>
          </div>

          {/* User List */}
          <div className="bg-white border border-divider rounded-xl shadow-sm overflow-hidden flex flex-col">
            <div className="bg-gray-50 px-6 py-4 border-b border-divider">
              <h2 className="text-lg font-bold text-ink">Active Staff</h2>
            </div>
            
            <div className="divide-y divide-divider flex-1 overflow-y-auto">
              {teachers.map(teacher => (
                <div key={teacher.id || teacher.email} className="px-6 py-4 hover:bg-gray-50 flex items-center justify-between">
                  <div>
                    <p className="font-bold text-ink text-sm">{teacher.name}</p>
                    <div className="flex items-center gap-1 text-xs text-graphite mt-1 font-secondary">
                      <Mail size={12} /> {teacher.email}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {teacher.isClassTeacher && (
                      <span className="px-2 py-1 rounded text-xs font-bold uppercase bg-green-100 text-green-700 whitespace-nowrap">
                        CT: {teacher.classTeacherOf} {teacher.classTeacherSection ? `- ${teacher.classTeacherSection}` : ''}
                      </span>
                    )}
                    <div className={`px-2 py-1 rounded text-xs font-bold uppercase whitespace-nowrap ${teacher.role === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                      {teacher.role}
                    </div>
                    {teacher.uid && (
                      <button
                        onClick={() => handleDeleteUser(teacher.uid, teacher.email)}
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors ml-2"
                        title="Delete User"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                </div>
              ))}

              {teachers.length === 0 && (
                <div className="px-6 py-8 text-center text-graphite text-sm">
                  No staff members synced yet.<br/>
                  Created accounts will appear here.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
